/**
 * The open times piece's standard look (the prototype's Open times band).
 * Draws the behavior half's state and calls its actions; holds no rule.
 * Draws nothing until there is at least one service.
 */

import { useId } from 'react';

import { partsOf, pieceRoot, usePlaced } from '../../contract/runtime';
import { onLink } from '../../ui/onLink';
import { formatDuration } from '../../format';
import shared from '../../styles/shared.module.css';
import { Icon } from '../../ui/Icon';
import type { OpenTimesRuntime } from './useOpenTimes';
import css from './OpenTimes.module.css';
import { contract } from './contract';

/** The note's default, about signing in: a signed-in client isn't shown it. A site's own note shows to everyone. */
const SIGN_IN_NOTE = contract.options.note.default;

const part = partsOf(contract);

export function OpenTimesView({ state, actions, options }: OpenTimesRuntime) {
  // Placed on a custom page (web-studio-sites D4): no band, no frame, no fixed anchor.
  const placed = usePlaced();
  const placedTitle = useId();
  const selected = state.selected;
  if (!selected) return null;
  const titleId = placed ? placedTitle : 'timesTitle';
  return (
    // data-band: a tinted band; the footer meets it with no gap when it ends the page.
    <section
      className={placed ? undefined : css.open}
      id={placed ? undefined : 'times'}
      data-band={placed ? undefined : 'tint'}
      aria-labelledby={options.heading ? titleId : undefined}
      aria-label={options.heading ? undefined : 'Open times'}
      {...pieceRoot(contract)}
      {...part('section')}
    >
      <div className={placed ? css.grid : `${shared.wrap} ${css.grid}`}>
        <div className={css.left}>
          {options.eyebrow && (
            <div className={shared.eyebrow} {...part('eyebrow')}>
              {options.eyebrow}
            </div>
          )}
          {options.heading && (
            <h2 className={`${shared.h2} ${css.heading}`} id={titleId} {...part('heading')}>
              {options.heading}
            </h2>
          )}
          {options.intro && (
            <p className={css.lead} {...part('intro')}>
              {options.intro}
            </p>
          )}
          <div className={css.tabs} role="group" aria-label="Service" {...part('tabs')}>
            {state.shown.map((s) => (
              <button
                key={s.id}
                type="button"
                className={css.tab}
                aria-pressed={s.id === selected.id}
                onClick={() => actions.selectService(s.id)}
                {...part('tab', s.id === selected.id && 'selected')}
              >
                {s.name}
                <small>{formatDuration(s.durationMinutes)}</small>
              </button>
            ))}
          </div>
          {state.pageCount > 1 && (
            <div className={css.pager} {...part('pager')}>
              <button
                type="button"
                className={css.pageArrow}
                aria-label="Earlier services"
                disabled={!state.canPrevious}
                onClick={actions.previous}
                {...part('previous', !state.canPrevious && 'disabled')}
              >
                <Icon name="left" />
              </button>
              <span className={css.range} aria-live="polite" {...part('range')}>
                {state.firstShown} to {state.firstShown + state.shown.length - 1} of {state.services.length}
              </span>
              <button
                type="button"
                className={css.pageArrow}
                aria-label="More services"
                disabled={!state.canNext}
                onClick={actions.next}
                {...part('next', !state.canNext && 'disabled')}
              >
                <Icon name="right" />
              </button>
            </div>
          )}
        </div>
        <div className={css.right}>
          <div className={css.head}>
            <div className={css.for} {...part('service')}>
              {selected.name}
              <small>{formatDuration(selected.durationMinutes)}</small>
            </div>
            <span className={css.tz} {...part('zone')}>
              {state.zoneName}
            </span>
          </div>
          {state.timesStatus === 'ready' && state.days.length > 0 && (
            <div className={css.days} {...part('days')}>
              {state.days.map((d) => (
                <div key={d.date} {...part('day')}>
                  <div className={css.dayH}>
                    <div className={css.dayN} {...part('dayName')}>
                      {d.name}
                    </div>
                    <div className={css.dayD} {...part('dayDate')}>
                      {d.dateLabel}
                    </div>
                  </div>
                  {d.times.map((t) => (
                    <a
                      key={t.startsAt}
                      className={css.slot}
                      href={t.href}
                      aria-label={`${d.dateLabel}, ${t.label}`}
                      onClick={onLink(() => actions.openTime(t.startsAt))}
                      {...part('time')}
                    >
                      {t.label}
                      <Icon name="arrow" className={css.slotIc} />
                    </a>
                  ))}
                </div>
              ))}
            </div>
          )}
          {state.timesStatus === 'ready' && state.days.length === 0 && (
            <p className={css.empty} {...part('empty')}>
              {selected.name} has no open times coming up.
            </p>
          )}
          {state.timesStatus === 'error' && (
            <p className={css.empty} {...part('empty')}>
              Open times could not load. Try again in a moment.
            </p>
          )}
          <div className={css.foot}>
            <a className={`${shared.go} ${css.seeAll}`} href={selected.path} onClick={onLink(actions.openService)} {...part('seeAll')}>
              <u>See every open time for {selected.name}</u>
              <Icon name="arrow" small />
            </a>
            {options.note && !(state.signedIn && options.note === SIGN_IN_NOTE) && (
              <span className={css.note} {...part('note')}>
                {options.note}
              </span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
